import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import CustomBreadcrumb from '../../../../components/customBreadcrumb/CustomBreadcrumb';
import CustomDonutChart, {
  IDonutChartData,
} from '../../../../components/customDonutChart/CustomDonutChart';
import CustomRadio from '../../../../components/customRadio/CustomRadio';
import EmptyPlaceholder from '../../../../components/emptyPlaceholder/EmptyPlaceholder';
import MainLoader from '../../../../components/loading/MainLoader';
import StatusAlertModel from '../../../../components/statusAlertModel/StatusAlertModel';
import Title from '../../../../components/title/Title';
import UsageSummeryContainer from '../../../../components/usageSummeryContainer/UsageSummeryContainer';
import {
  IBroadbandProtocolData,
  IBroadbandProtocolDataUsageItem,
} from '../../../../interfaces/broadbandProtocolData';
import { PROFILE_ACTIONS } from '../../../../interfaces/profileData';
import { ISuccessState } from '../../../../interfaces/successState';
import { getBroadbandProtocolData } from '../../../../services/dashboard';
import { getDataWithCheckingActionPrivilege } from '../../../../util/hasActionPrivileges';
import { chartColors, otherColors } from '../ProtocolReport';

const BroadbandReport = () => {
  const { serviceId, date } = useParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRadioIndex, setSelectedRadioIndex] = useState(0);
  const [isSuccess, setIsSuccess] = useState<ISuccessState | undefined>();
  const [usageData, setUsageData] = useState<IDonutChartData[]>([]);
  const [protocolData, setProtocolData] = useState<IBroadbandProtocolData>();

  useEffect(() => {
    if (serviceId && date) {
      setLoading(true);
      getDataWithCheckingActionPrivilege(
        true,
        serviceId,
        PROFILE_ACTIONS.PROTOCOL_REPORT,
        () => getBroadbandProtocolData(serviceId, date)
      )
        .then((data: IBroadbandProtocolData) => {
          setProtocolData(data);
          const transformedData = transformData(selectedRadioIndex, data);
          setUsageData(transformedData);
          setLoading(false);
        })
        .catch((err) => {
          setIsSuccess({ isSuccess: false, message: err.message });
          setLoading(false);
          setUsageData([]);
        });
    }
  }, [selectedRadioIndex, serviceId, date]);

  const transformData = (
    index: number,
    data: IBroadbandProtocolData
  ): IDonutChartData[] => {
    let key: keyof IBroadbandProtocolData = 'total';
    if (index === 1) {
      key = 'download';
    }
    if (index === 2) {
      key = 'upload';
    }

    const protocolUsage: IBroadbandProtocolDataUsageItem[] = data[key]
      .filter((value) => value.presentage > 1)
      .sort(
        (
          a: IBroadbandProtocolDataUsageItem,
          b: IBroadbandProtocolDataUsageItem
        ) => b.presentage - a.presentage
      );

    let randomColorIndex = 0;
    const usageWithColors: IDonutChartData[] = protocolUsage.map(
      (item: IBroadbandProtocolDataUsageItem) => {
        let color: string | undefined = chartColors.find(
          (platform) => platform.platform === item.protocol.toLowerCase()
        )?.color;
        if (!color) {
          color = otherColors[randomColorIndex];
          randomColorIndex++;
        }

        return {
          title: item.protocol,
          value: +item.presentage.toFixed(1),
          color,
        };
      }
    );

    return usageWithColors;
  };

  const radioIndexChangeHandler = (index: number) => {
    setSelectedRadioIndex(index);
    setUsageData([]);
    if (protocolData) {
      const transformedData = transformData(index, protocolData);
      setUsageData(transformedData);
    }
  };

  return (
    <div className='page-container'>
      <MainLoader show={loading} />
      <CustomBreadcrumb
        items={[
          { path: '/', title: 'Home' },
          { path: '', title: 'Protocol Report ' + date },
        ]}
      />

      <Title>Protocol Report</Title>
      {usageData.length > 0 ? (
        <UsageSummeryContainer title={`Protocol Report ${serviceId}: ${date}`}>
          <Row className='justify-content-center mt-4'>
            <Col className='col-md-10 col-12'>
              <CustomRadio
                selectedIndex={selectedRadioIndex}
                onChange={radioIndexChangeHandler}
                items={[
                  {
                    label: 'Total',
                    index: 0,
                  },
                  {
                    label: 'Download',
                    index: 1,
                  },
                  {
                    label: 'Upload',
                    index: 2,
                  },
                ]}
              />
              {usageData && <CustomDonutChart usageData={usageData} />}
            </Col>
          </Row>
        </UsageSummeryContainer>
      ) : (
        <Row className='justify-content-center'>
          <Col className='col-md-4 mt-4 d-none d-md-block'>
            <EmptyPlaceholder />
          </Col>
        </Row>
      )}
      <StatusAlertModel isSuccess={isSuccess} setIsSuccess={setIsSuccess} />
    </div>
  );
};

export default BroadbandReport;
